/**
 * Language detection + Edge TTS voice mapping.
 *
 * Uses tinyld for lightweight client-side language detection (no API calls).
 * Maps detected language to the best available Edge TTS voice, with support
 * for gendered voices, per-language overrides, and preferred-language filtering.
 */

import { detect } from 'tinyld';

// ── Supported Languages ──

export interface LanguageInfo {
  code: string;       // ISO 639-1
  name: string;       // Display name
  flag: string;       // Emoji flag
  femaleVoice: string;
  maleVoice: string;
}

export const SUPPORTED_LANGUAGES: LanguageInfo[] = [
  { code: 'en', name: 'English',    flag: '🇬🇧', femaleVoice: 'en-US-AvaNeural',      maleVoice: 'en-US-AndrewNeural' },
  { code: 'da', name: 'Danish',     flag: '🇩🇰', femaleVoice: 'da-DK-ChristelNeural',  maleVoice: 'da-DK-JeppeNeural' },
  { code: 'de', name: 'German',     flag: '🇩🇪', femaleVoice: 'de-DE-KatjaNeural',     maleVoice: 'de-DE-ConradNeural' },
  { code: 'fr', name: 'French',     flag: '🇫🇷', femaleVoice: 'fr-FR-DeniseNeural',    maleVoice: 'fr-FR-HenriNeural' },
  { code: 'es', name: 'Spanish',    flag: '🇪🇸', femaleVoice: 'es-ES-ElviraNeural',    maleVoice: 'es-ES-AlvaroNeural' },
  { code: 'it', name: 'Italian',    flag: '🇮🇹', femaleVoice: 'it-IT-ElsaNeural',      maleVoice: 'it-IT-DiegoNeural' },
  { code: 'pt', name: 'Portuguese', flag: '🇧🇷', femaleVoice: 'pt-BR-FranciscaNeural', maleVoice: 'pt-BR-AntonioNeural' },
  { code: 'nl', name: 'Dutch',      flag: '🇳🇱', femaleVoice: 'nl-NL-ColetteNeural',   maleVoice: 'nl-NL-MaartenNeural' },
  { code: 'sv', name: 'Swedish',    flag: '🇸🇪', femaleVoice: 'sv-SE-SofieNeural',     maleVoice: 'sv-SE-MattiasNeural' },
  { code: 'nb', name: 'Norwegian',  flag: '🇳🇴', femaleVoice: 'nb-NO-PernilleNeural',  maleVoice: 'nb-NO-FinnNeural' },
  { code: 'fi', name: 'Finnish',    flag: '🇫🇮', femaleVoice: 'fi-FI-NooraNeural',     maleVoice: 'fi-FI-HarriNeural' },
  { code: 'pl', name: 'Polish',     flag: '🇵🇱', femaleVoice: 'pl-PL-ZofiaNeural',     maleVoice: 'pl-PL-MarekNeural' },
  { code: 'ja', name: 'Japanese',   flag: '🇯🇵', femaleVoice: 'ja-JP-NanamiNeural',    maleVoice: 'ja-JP-KeitaNeural' },
  { code: 'ko', name: 'Korean',     flag: '🇰🇷', femaleVoice: 'ko-KR-SunHiNeural',    maleVoice: 'ko-KR-InJoonNeural' },
  { code: 'zh', name: 'Chinese',    flag: '🇨🇳', femaleVoice: 'zh-CN-XiaoxiaoNeural',  maleVoice: 'zh-CN-YunxiNeural' },
  { code: 'ru', name: 'Russian',    flag: '🇷🇺', femaleVoice: 'ru-RU-SvetlanaNeural',  maleVoice: 'ru-RU-DmitryNeural' },
  { code: 'ar', name: 'Arabic',     flag: '🇸🇦', femaleVoice: 'ar-SA-ZariyahNeural',   maleVoice: 'ar-SA-HamedNeural' },
  { code: 'hi', name: 'Hindi',      flag: '🇮🇳', femaleVoice: 'hi-IN-SwaraNeural',     maleVoice: 'hi-IN-MadhurNeural' },
  { code: 'tr', name: 'Turkish',    flag: '🇹🇷', femaleVoice: 'tr-TR-EmelNeural',      maleVoice: 'tr-TR-AhmetNeural' },
  { code: 'uk', name: 'Ukrainian',  flag: '🇺🇦', femaleVoice: 'uk-UA-PolinaNeural',    maleVoice: 'uk-UA-OstapNeural' },
  { code: 'cs', name: 'Czech',      flag: '🇨🇿', femaleVoice: 'cs-CZ-VlastaNeural',    maleVoice: 'cs-CZ-AntoninNeural' },
  { code: 'el', name: 'Greek',      flag: '🇬🇷', femaleVoice: 'el-GR-AthinaNeural',    maleVoice: 'el-GR-NestorasNeural' },
  { code: 'ro', name: 'Romanian',   flag: '🇷🇴', femaleVoice: 'ro-RO-AlinaNeural',     maleVoice: 'ro-RO-EmilNeural' },
  { code: 'hu', name: 'Hungarian',  flag: '🇭🇺', femaleVoice: 'hu-HU-NoemiNeural',     maleVoice: 'hu-HU-TamasNeural' },
  { code: 'th', name: 'Thai',       flag: '🇹🇭', femaleVoice: 'th-TH-PremwadeeNeural', maleVoice: 'th-TH-NiwatNeural' },
  { code: 'vi', name: 'Vietnamese', flag: '🇻🇳', femaleVoice: 'vi-VN-HoaiMyNeural',    maleVoice: 'vi-VN-NamMinhNeural' },
];

// ── Voice Catalog ──

export interface VoiceOption {
  name: string;       // Full Edge TTS voice name, e.g., 'en-US-AvaNeural'
  display: string;    // Friendly display name, e.g., 'Ava (US)'
  gender: 'female' | 'male';
  locale: string;     // e.g., 'en-US'
}

/** ALL available Edge TTS Neural voices per supported language code */
export const VOICE_CATALOG: Record<string, VoiceOption[]> = {
  en: [
    // Female
    { name: 'en-US-AvaNeural', display: 'Ava (US)', gender: 'female', locale: 'en-US' },
    { name: 'en-US-AvaMultilingualNeural', display: 'Ava Multilingual (US)', gender: 'female', locale: 'en-US' },
    { name: 'en-US-AriaNeural', display: 'Aria (US)', gender: 'female', locale: 'en-US' },
    { name: 'en-US-AnaNeural', display: 'Ana (US)', gender: 'female', locale: 'en-US' },
    { name: 'en-US-EmmaNeural', display: 'Emma (US)', gender: 'female', locale: 'en-US' },
    { name: 'en-US-EmmaMultilingualNeural', display: 'Emma Multilingual (US)', gender: 'female', locale: 'en-US' },
    { name: 'en-US-JennyNeural', display: 'Jenny (US)', gender: 'female', locale: 'en-US' },
    { name: 'en-US-MichelleNeural', display: 'Michelle (US)', gender: 'female', locale: 'en-US' },
    { name: 'en-GB-LibbyNeural', display: 'Libby (GB)', gender: 'female', locale: 'en-GB' },
    { name: 'en-GB-MaisieNeural', display: 'Maisie (GB)', gender: 'female', locale: 'en-GB' },
    { name: 'en-GB-SoniaNeural', display: 'Sonia (GB)', gender: 'female', locale: 'en-GB' },
    { name: 'en-AU-NatashaNeural', display: 'Natasha (AU)', gender: 'female', locale: 'en-AU' },
    { name: 'en-CA-ClaraNeural', display: 'Clara (CA)', gender: 'female', locale: 'en-CA' },
    { name: 'en-IE-EmilyNeural', display: 'Emily (IE)', gender: 'female', locale: 'en-IE' },
    { name: 'en-IN-NeerjaNeural', display: 'Neerja (IN)', gender: 'female', locale: 'en-IN' },
    { name: 'en-NZ-MollyNeural', display: 'Molly (NZ)', gender: 'female', locale: 'en-NZ' },
    // Male
    { name: 'en-US-AndrewNeural', display: 'Andrew (US)', gender: 'male', locale: 'en-US' },
    { name: 'en-US-AndrewMultilingualNeural', display: 'Andrew Multilingual (US)', gender: 'male', locale: 'en-US' },
    { name: 'en-US-BrianNeural', display: 'Brian (US)', gender: 'male', locale: 'en-US' },
    { name: 'en-US-BrianMultilingualNeural', display: 'Brian Multilingual (US)', gender: 'male', locale: 'en-US' },
    { name: 'en-US-ChristopherNeural', display: 'Christopher (US)', gender: 'male', locale: 'en-US' },
    { name: 'en-US-EricNeural', display: 'Eric (US)', gender: 'male', locale: 'en-US' },
    { name: 'en-US-GuyNeural', display: 'Guy (US)', gender: 'male', locale: 'en-US' },
    { name: 'en-US-RogerNeural', display: 'Roger (US)', gender: 'male', locale: 'en-US' },
    { name: 'en-US-SteffanNeural', display: 'Steffan (US)', gender: 'male', locale: 'en-US' },
    { name: 'en-GB-RyanNeural', display: 'Ryan (GB)', gender: 'male', locale: 'en-GB' },
    { name: 'en-GB-ThomasNeural', display: 'Thomas (GB)', gender: 'male', locale: 'en-GB' },
    { name: 'en-AU-WilliamMultilingualNeural', display: 'William (AU)', gender: 'male', locale: 'en-AU' },
    { name: 'en-CA-LiamNeural', display: 'Liam (CA)', gender: 'male', locale: 'en-CA' },
    { name: 'en-IE-ConnorNeural', display: 'Connor (IE)', gender: 'male', locale: 'en-IE' },
    { name: 'en-IN-PrabhatNeural', display: 'Prabhat (IN)', gender: 'male', locale: 'en-IN' },
    { name: 'en-NZ-MitchellNeural', display: 'Mitchell (NZ)', gender: 'male', locale: 'en-NZ' },
  ],
  da: [
    { name: 'da-DK-ChristelNeural', display: 'Christel', gender: 'female', locale: 'da-DK' },
    { name: 'da-DK-JeppeNeural', display: 'Jeppe', gender: 'male', locale: 'da-DK' },
  ],
  de: [
    { name: 'de-DE-KatjaNeural', display: 'Katja', gender: 'female', locale: 'de-DE' },
    { name: 'de-DE-AmalaNeural', display: 'Amala', gender: 'female', locale: 'de-DE' },
    { name: 'de-DE-SeraphinaMultilingualNeural', display: 'Seraphina', gender: 'female', locale: 'de-DE' },
    { name: 'de-DE-ConradNeural', display: 'Conrad', gender: 'male', locale: 'de-DE' },
    { name: 'de-DE-FlorianMultilingualNeural', display: 'Florian', gender: 'male', locale: 'de-DE' },
    { name: 'de-DE-KillianNeural', display: 'Killian', gender: 'male', locale: 'de-DE' },
  ],
  fr: [
    { name: 'fr-FR-DeniseNeural', display: 'Denise', gender: 'female', locale: 'fr-FR' },
    { name: 'fr-FR-EloiseNeural', display: 'Eloise', gender: 'female', locale: 'fr-FR' },
    { name: 'fr-FR-VivienneMultilingualNeural', display: 'Vivienne', gender: 'female', locale: 'fr-FR' },
    { name: 'fr-FR-HenriNeural', display: 'Henri', gender: 'male', locale: 'fr-FR' },
    { name: 'fr-FR-RemyMultilingualNeural', display: 'Remy', gender: 'male', locale: 'fr-FR' },
  ],
  es: [
    { name: 'es-ES-ElviraNeural', display: 'Elvira', gender: 'female', locale: 'es-ES' },
    { name: 'es-ES-XimenaNeural', display: 'Ximena', gender: 'female', locale: 'es-ES' },
    { name: 'es-ES-AlvaroNeural', display: 'Alvaro', gender: 'male', locale: 'es-ES' },
  ],
  it: [
    { name: 'it-IT-ElsaNeural', display: 'Elsa', gender: 'female', locale: 'it-IT' },
    { name: 'it-IT-IsabellaNeural', display: 'Isabella', gender: 'female', locale: 'it-IT' },
    { name: 'it-IT-DiegoNeural', display: 'Diego', gender: 'male', locale: 'it-IT' },
    { name: 'it-IT-GiuseppeMultilingualNeural', display: 'Giuseppe', gender: 'male', locale: 'it-IT' },
  ],
  pt: [
    { name: 'pt-BR-FranciscaNeural', display: 'Francisca', gender: 'female', locale: 'pt-BR' },
    { name: 'pt-BR-ThalitaMultilingualNeural', display: 'Thalita', gender: 'female', locale: 'pt-BR' },
    { name: 'pt-BR-AntonioNeural', display: 'Antonio', gender: 'male', locale: 'pt-BR' },
  ],
  nl: [
    { name: 'nl-NL-ColetteNeural', display: 'Colette', gender: 'female', locale: 'nl-NL' },
    { name: 'nl-NL-FennaNeural', display: 'Fenna', gender: 'female', locale: 'nl-NL' },
    { name: 'nl-NL-MaartenNeural', display: 'Maarten', gender: 'male', locale: 'nl-NL' },
  ],
  sv: [
    { name: 'sv-SE-SofieNeural', display: 'Sofie', gender: 'female', locale: 'sv-SE' },
    { name: 'sv-SE-MattiasNeural', display: 'Mattias', gender: 'male', locale: 'sv-SE' },
  ],
  nb: [
    { name: 'nb-NO-PernilleNeural', display: 'Pernille', gender: 'female', locale: 'nb-NO' },
    { name: 'nb-NO-FinnNeural', display: 'Finn', gender: 'male', locale: 'nb-NO' },
  ],
  fi: [
    { name: 'fi-FI-NooraNeural', display: 'Noora', gender: 'female', locale: 'fi-FI' },
    { name: 'fi-FI-HarriNeural', display: 'Harri', gender: 'male', locale: 'fi-FI' },
  ],
  pl: [
    { name: 'pl-PL-ZofiaNeural', display: 'Zofia', gender: 'female', locale: 'pl-PL' },
    { name: 'pl-PL-MarekNeural', display: 'Marek', gender: 'male', locale: 'pl-PL' },
  ],
  ja: [
    { name: 'ja-JP-NanamiNeural', display: 'Nanami', gender: 'female', locale: 'ja-JP' },
    { name: 'ja-JP-KeitaNeural', display: 'Keita', gender: 'male', locale: 'ja-JP' },
  ],
  ko: [
    { name: 'ko-KR-SunHiNeural', display: 'SunHi', gender: 'female', locale: 'ko-KR' },
    { name: 'ko-KR-InJoonNeural', display: 'InJoon', gender: 'male', locale: 'ko-KR' },
    { name: 'ko-KR-HyunsuMultilingualNeural', display: 'Hyunsu', gender: 'male', locale: 'ko-KR' },
  ],
  zh: [
    { name: 'zh-CN-XiaoxiaoNeural', display: 'Xiaoxiao', gender: 'female', locale: 'zh-CN' },
    { name: 'zh-CN-XiaoyiNeural', display: 'Xiaoyi', gender: 'female', locale: 'zh-CN' },
    { name: 'zh-CN-YunxiNeural', display: 'Yunxi', gender: 'male', locale: 'zh-CN' },
    { name: 'zh-CN-YunjianNeural', display: 'Yunjian', gender: 'male', locale: 'zh-CN' },
    { name: 'zh-CN-YunyangNeural', display: 'Yunyang', gender: 'male', locale: 'zh-CN' },
    { name: 'zh-CN-YunxiaNeural', display: 'Yunxia', gender: 'male', locale: 'zh-CN' },
  ],
  ru: [
    { name: 'ru-RU-SvetlanaNeural', display: 'Svetlana', gender: 'female', locale: 'ru-RU' },
    { name: 'ru-RU-DmitryNeural', display: 'Dmitry', gender: 'male', locale: 'ru-RU' },
  ],
  ar: [
    { name: 'ar-SA-ZariyahNeural', display: 'Zariyah', gender: 'female', locale: 'ar-SA' },
    { name: 'ar-SA-HamedNeural', display: 'Hamed', gender: 'male', locale: 'ar-SA' },
  ],
  hi: [
    { name: 'hi-IN-SwaraNeural', display: 'Swara', gender: 'female', locale: 'hi-IN' },
    { name: 'hi-IN-MadhurNeural', display: 'Madhur', gender: 'male', locale: 'hi-IN' },
  ],
  tr: [
    { name: 'tr-TR-EmelNeural', display: 'Emel', gender: 'female', locale: 'tr-TR' },
    { name: 'tr-TR-AhmetNeural', display: 'Ahmet', gender: 'male', locale: 'tr-TR' },
  ],
  uk: [
    { name: 'uk-UA-PolinaNeural', display: 'Polina', gender: 'female', locale: 'uk-UA' },
    { name: 'uk-UA-OstapNeural', display: 'Ostap', gender: 'male', locale: 'uk-UA' },
  ],
  cs: [
    { name: 'cs-CZ-VlastaNeural', display: 'Vlasta', gender: 'female', locale: 'cs-CZ' },
    { name: 'cs-CZ-AntoninNeural', display: 'Antonin', gender: 'male', locale: 'cs-CZ' },
  ],
  el: [
    { name: 'el-GR-AthinaNeural', display: 'Athina', gender: 'female', locale: 'el-GR' },
    { name: 'el-GR-NestorasNeural', display: 'Nestoras', gender: 'male', locale: 'el-GR' },
  ],
  ro: [
    { name: 'ro-RO-AlinaNeural', display: 'Alina', gender: 'female', locale: 'ro-RO' },
    { name: 'ro-RO-EmilNeural', display: 'Emil', gender: 'male', locale: 'ro-RO' },
  ],
  hu: [
    { name: 'hu-HU-NoemiNeural', display: 'Noemi', gender: 'female', locale: 'hu-HU' },
    { name: 'hu-HU-TamasNeural', display: 'Tamas', gender: 'male', locale: 'hu-HU' },
  ],
  th: [
    { name: 'th-TH-PremwadeeNeural', display: 'Premwadee', gender: 'female', locale: 'th-TH' },
    { name: 'th-TH-NiwatNeural', display: 'Niwat', gender: 'male', locale: 'th-TH' },
  ],
  vi: [
    { name: 'vi-VN-HoaiMyNeural', display: 'HoaiMy', gender: 'female', locale: 'vi-VN' },
    { name: 'vi-VN-NamMinhNeural', display: 'NamMinh', gender: 'male', locale: 'vi-VN' },
  ],
};

/** Get all voices for a language, optionally filtered by gender */
export function getVoicesForLanguage(langCode: string, gender?: 'female' | 'male'): VoiceOption[] {
  const voices = VOICE_CATALOG[langCode] ?? [];
  if (!gender) return voices;
  return voices.filter(v => v.gender === gender);
}

/** Sample phrases per language for voice preview (use {name} placeholder) */
export const PREVIEW_PHRASES: Record<string, string> = {
  en: "Hello! I'm {name}. How can I help you today?",
  da: 'Hej! Jeg hedder {name}. Hvordan kan jeg hjælpe dig?',
  de: 'Hallo! Ich bin {name}. Wie kann ich Ihnen helfen?',
  fr: "Bonjour! Je m'appelle {name}. Comment puis-je vous aider?",
  es: '¡Hola! Soy {name}. ¿Cómo puedo ayudarte?',
  it: 'Ciao! Sono {name}. Come posso aiutarti?',
  pt: 'Olá! Eu sou {name}. Como posso ajudá-lo?',
  nl: 'Hallo! Ik ben {name}. Hoe kan ik u helpen?',
  sv: 'Hej! Jag heter {name}. Hur kan jag hjälpa dig?',
  nb: 'Hei! Jeg heter {name}. Hvordan kan jeg hjelpe deg?',
  fi: 'Hei! Olen {name}. Miten voin auttaa sinua?',
  pl: 'Cześć! Jestem {name}. Jak mogę ci pomóc?',
  ja: 'こんにちは！{name}です。何かお手伝いできますか？',
  ko: '안녕하세요! {name}입니다. 어떻게 도와드릴까요?',
  zh: '你好！我是{name}。有什么可以帮助你的吗？',
  ru: 'Привет! Я {name}. Чем могу помочь?',
  ar: 'مرحباً! أنا {name}. كيف يمكنني مساعدتك؟',
  hi: 'नमस्ते! मैं {name} हूँ। मैं आपकी कैसे मदद कर सकती हूँ?',
  tr: 'Merhaba! Ben {name}. Size nasıl yardımcı olabilirim?',
  uk: 'Привіт! Я {name}. Чим можу допомогти?',
  cs: 'Ahoj! Jsem {name}. Jak vám mohu pomoci?',
  el: 'Γεια! Είμαι η {name}. Πώς μπορώ να σας βοηθήσω;',
  ro: 'Bună! Sunt {name}. Cum te pot ajuta?',
  hu: 'Szia! {name} vagyok. Miben segíthetek?',
  th: 'สวัสดีค่ะ! ฉันชื่อ {name} มีอะไรให้ช่วยไหมคะ?',
  vi: 'Xin chào! Tôi là {name}. Tôi có thể giúp gì cho bạn?',
};

// Build lookup map from the array
const LANG_MAP = new Map<string, LanguageInfo>(SUPPORTED_LANGUAGES.map(l => [l.code, l]));

// Keep backward compat: `no` → `nb`
const nbInfo = LANG_MAP.get('nb');
if (nbInfo) LANG_MAP.set('no', nbInfo);

const DEFAULT_EDGE_VOICE = 'en-US-AvaNeural';

/** Minimum text length for reliable language detection */
const MIN_DETECT_LENGTH = 10;

// ── Settings Helpers ──

/** Get user's preferred languages from localStorage */
export function getPreferredLanguages(): string[] {
  try {
    const raw = localStorage.getItem('talkyn-preferred-languages');
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed as string[];
    }
  } catch { /* ignore */ }
  return ['en'];
}

/** Get "Also Reply In" languages from localStorage */
export function getAlsoReplyInLanguages(): string[] {
  try {
    const raw = localStorage.getItem('talkyn-also-reply-in');
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed as string[];
    }
  } catch { /* ignore */ }
  return [];
}

/** Get voice gender preference */
export function getVoiceGender(): 'female' | 'male' {
  const val = localStorage.getItem('talkyn-voice-gender');
  return val === 'male' ? 'male' : 'female';
}

/** Get per-language voice overrides */
export function getVoiceOverrides(): Record<string, string> {
  try {
    const raw = localStorage.getItem('talkyn-voice-overrides');
    if (raw) return JSON.parse(raw) as Record<string, string>;
  } catch { /* ignore */ }
  return {};
}

/** Get all active languages (preferred + also-reply-in, deduplicated) */
export function getAllActiveLanguages(): string[] {
  const preferred = getPreferredLanguages();
  const alsoReply = getAlsoReplyInLanguages();
  return [...new Set([...preferred, ...alsoReply])];
}

/**
 * Get the Edge TTS voice for a given language code, respecting gender and overrides.
 */
export function getVoiceForLanguage(langCode: string): string {
  const overrides = getVoiceOverrides();
  if (overrides[langCode]) return overrides[langCode];

  const langInfo = LANG_MAP.get(langCode);
  if (!langInfo) return DEFAULT_EDGE_VOICE;

  const gender = getVoiceGender();
  return gender === 'male' ? langInfo.maleVoice : langInfo.femaleVoice;
}

// ── Detection ──

/**
 * Detect the language of the given text, filtered by user's preferred languages.
 *
 * Returns an ISO 639-1 code (e.g. 'en', 'da', 'de').
 * Falls back to the first preferred language for very short text or when
 * detection fails or the detected language isn't in the active language list.
 */
export function detectLanguage(text: string): string {
  const preferred = getPreferredLanguages();

  if (!text || text.trim().length < MIN_DETECT_LENGTH) {
    return preferred[0] ?? 'en';
  }

  try {
    const lang = detect(text);
    if (!lang) return preferred[0] ?? 'en';

    // If detected language is in preferred list, use it
    if (preferred.includes(lang)) return lang;

    // Handle Norwegian variant: 'no' → 'nb'
    if (lang === 'no' && preferred.includes('nb')) return 'nb';
    if (lang === 'nb' && preferred.includes('no')) return 'nb';

    // If detected language is in "Also Reply In", use it (for TTS output)
    const alsoReply = getAlsoReplyInLanguages();
    if (alsoReply.includes(lang)) return lang;

    // Not in any active language list — default to first preferred
    return preferred[0] ?? 'en';
  } catch {
    return preferred[0] ?? 'en';
  }
}

/**
 * Get the best Edge TTS voice for the given text based on detected language.
 *
 * Detects the language of the text, then resolves the voice using gender
 * preference and per-language overrides.
 */
export function getEdgeVoiceForText(text: string): string {
  const active = getAllActiveLanguages();
  if (active.length <= 1) return getVoiceForLanguage(active[0] ?? 'en');

  const lang = detectLanguage(text);
  return getVoiceForLanguage(lang);
}

// ── Voice Lock ──

/**
 * Stateful language detection manager that prevents mid-response voice switching.
 *
 * Buffers words before committing to a voice, then locks to that voice for the
 * remainder of the response. Only switches if >= 5 consecutive words in a
 * different language are detected. Resets on each new response (runId).
 */
export class VoiceLockManager {
  private lockedVoice: string | null = null;
  private wordBuffer: string[] = [];
  private consecutiveDifferentWords: string[] = [];
  private currentRunId: string | null = null;

  reset(runId?: string): void {
    this.lockedVoice = null;
    this.wordBuffer = [];
    this.consecutiveDifferentWords = [];
    this.currentRunId = runId ?? null;
  }

  /**
   * Get the voice to use for the given text.
   *
   * @returns The voice string, or null if still buffering (not enough words yet).
   */
  getVoice(text: string, runId?: string): string | null {
    if (runId !== undefined && runId !== this.currentRunId) {
      this.reset(runId);
    }

    const active = getAllActiveLanguages();
    if (active.length <= 1) {
      this.lockedVoice = getVoiceForLanguage(active[0] ?? 'en');
      return this.lockedVoice;
    }

    const words = text.trim().split(/\s+/).filter(Boolean);
    this.wordBuffer.push(...words);

    if (this.lockedVoice === null) {
      if (this.wordBuffer.length < 5) {
        return null;
      }
      const bufferText = this.wordBuffer.join(' ');
      const lang = detectLanguage(bufferText);
      this.lockedVoice = getVoiceForLanguage(lang);
      return this.lockedVoice;
    }

    // Already have a locked voice — check if this chunk wants to switch
    const lang = detectLanguage(text);
    const detectedVoice = getVoiceForLanguage(lang);

    if (detectedVoice !== this.lockedVoice) {
      this.consecutiveDifferentWords.push(...words);
      if (this.consecutiveDifferentWords.length >= 5) {
        this.lockedVoice = detectedVoice;
        this.consecutiveDifferentWords = [];
      }
    } else {
      this.consecutiveDifferentWords = [];
    }

    return this.lockedVoice;
  }
}
